import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, AlertTitle, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BlockIcon from '@mui/icons-material/Block';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import LanIcon from '@mui/icons-material/Lan';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../../animations/variants';
import {
  CK, FONT_BODY, FONT_MONO, actionRowSx, bodyCellSx, cidrCellSx, dialogProps, dialogTitleSx,
  emptyTextSx, headCellSx, insetPanelSx, listCardSx, rowSx, sectionCardSx,
  statusChipSx, tableHeadRowSx,
} from '../../portalStyles';
import PortalPageHeader from '../../../../components/portal/PortalPageHeader';
import {
  PortalErrorState, PortalLoading, PortalSectionHeader,
} from '../../../../components/portal/PortalStates';
import {
  allocateIpamPoolCidr, createIpamPoolShare, deleteIpamPool, deleteIpamPoolShare,
  deprovisionIpamPoolCidr, getIpamConfig, getIpamPool, listIpamPoolAllocations,
  listIpamPoolCidrs, listIpamPoolShares, listIpamScopes, provisionIpamPoolCidr,
  releaseIpamPoolAllocation, updateIpamPool,
} from '../../../../services/ipamApi';
import { IPAM_ISOLATED_SENTENCE, IpamStateChip, ipamStateIsIsolated, ipamStateMeta } from './ipamState';
import {
  IPAM_NEVER_DELETES_YOUR_RESOURCES, IPAM_NO_VPC_DELETED,
  IPAM_POOL_ALLOCATIONS_EMPTY, IPAM_POOL_ALLOCATIONS_SUBTITLE,
  IPAM_POOL_EDIT_NEEDS_LIVE_IPAM, IPAM_POOL_EDIT_READ_ONLY, IPAM_POOL_NO_PRISM_BLOCKERS,
  ipamPoolLedgerCaveat,
} from './ipamCopy';
// Props are { open, onClose, cidr, scopeId, scopeLabel }. `cidr` and `scopeId`
// are both REQUIRED for a lookup — the drawer renders "nothing to look up" and
// makes no request if either is falsy — because AWS keys address history by CIDR
// AND scope, and the same CIDR can legitimately exist in two of them. scopeLabel
// is optional and captions the drawer, which is why the scope list is loaded
// here at all: without it the caption can only name an `ipam-scope-…` id.
import IpamHistoryDrawer from './IpamHistoryDrawer';

// VPC IPAM — one pool: what is in it, who it is shared with, and what stands in
// the way of deleting it.
//
// ── WHY THIS IS A PAGE AND NOT A MODAL ──────────────────────────────────────
//
// A pool id is the unit an operator pastes into a ticket, a runbook and a chat
// thread. `/securityhub/ipam/pools/ipam-pool-0123…` has to resolve on its own,
// from a cold browser, with no tree state behind it. A modal cannot be linked
// to, cannot be bookmarked, and cannot be reached from a page reload — and this
// screen carries four tables and two irreversible actions, which is more than a
// modal can hold honestly anyway.
//
// ── THE TWO AWS BEHAVIOURS THIS SCREEN'S COPY IS BUILT AROUND ───────────────
//
// 1. ReleaseIpamPoolAllocation GENUINELY REFUSES a resource-backed allocation.
//    There is no force flag and no override; AWS's own answer is "delete the
//    resource", and Prism will never delete a resource in a customer's account.
//    So the release button is not merely disabled — it is disabled with the
//    reason, because a greyed button with no explanation reads as a permission
//    problem the operator will spend an hour trying to solve.
//
// 2. AFTER the VPC is deleted the allocation persists as a GHOST for several
//    minutes, and release still refuses during that window. "Try again in a few
//    minutes" is therefore literal, verified advice on this screen rather than
//    the thing support says when it has nothing.
//
// Both are why a delete refusal here NAMES its blockers. "The pool still has
// allocations" is unactionable; "delete vpc-068e2835b7501db47 in account
// 857639285487 (us-east-1)" is the operator's next action, and it may not even
// be their own account — which is exactly why the account id has to be on
// screen.

const IPAM_COLOR = '#455A64';
const IPAM_COLOR_DARK = '#31434B';

// The promise, spelled once. It is the sentence that turns a refusal from "Prism
// is broken" into "Prism is behaving correctly and here is the door".
// Re-exported from the one module that owns the verbatim copy; kept as a local
// alias only so the many call sites below read the same as before.
const NEVER_DELETE_SENTENCE = IPAM_NEVER_DELETES_YOUR_RESOURCES;

// AWS's stored AllocationSource vocabulary: `manual` for an allocation someone
// reserved by hand (AWS's ResourceType "custom"), `resource` for one AWS created
// because a real VPC or subnet took the space. It is written at ingest and never
// re-derived, and it is the single fact that decides whether release is possible.
const SOURCE_RESOURCE = 'resource';

const rowsOf = (res) => (Array.isArray(res?.data) ? res.data : []);
const dash = (v) => (v === null || v === undefined || String(v).trim() === '' ? '—' : v);

const Mono = ({ children }) => (
  <Box component="span" sx={{ fontFamily: FONT_MONO, fontSize: '0.8125rem' }}>{children}</Box>
);

const Muted = ({ title, children = '—' }) =>
  title ? (
    <Tooltip title={title}>
      <Box component="span" sx={{ color: CK.muted, fontFamily: FONT_BODY }}>{children}</Box>
    </Tooltip>
  ) : (
    <Box component="span" sx={{ color: CK.muted, fontFamily: FONT_BODY }}>{children}</Box>
  );

// A CIDR row still occupies its pool's quota unless AWS says `deprovisioned`.
// `pending-deprovision` and `failed-deprovision` BOTH still hold the CIDR, so a
// "CIDRs in this pool" count that treated them as gone would understate what the
// pool is actually holding — and failed-deprovision is retryable, not terminal.
const cidrIsLive = (state) => String(state || '').trim().toLowerCase() !== 'deprovisioned';

// A resource-backed allocation is the thing that blocks a delete. Derived from
// the STORED AllocationSource, never re-derived from the resource type here —
// one mapper server-side, one reader client-side.
const isResourceBacked = (allocation) =>
  String(allocation?.allocationSource || '').trim().toLowerCase() === SOURCE_RESOURCE;

// ── Named blockers ──────────────────────────────────────────────────────────
//
// The backend answers a blocked delete with HTTP 409 and a body carrying
// `blockers: [{allocationId, cidr, resourceId, resourceType, accountId}]`
// alongside the message.
//
// portalFetch used to DISCARD everything on a non-2xx body except
// `message || error`, so the structured array never reached this page. It now
// attaches the parsed body as `err.body`, so the 409's blockers survive — but
// the fallback below is kept, and not as belt-and-braces:
//
//   * the backend's own sentence is rendered VERBATIM regardless, because it
//     names every blocker in prose and is the carrier that definitely arrives;
//   * when the structured array is absent the table is built from the
//     allocations THIS PAGE ALREADY LOADED, filtered to the resource-backed
//     ones — the same set the backend derives its blockers from, and which
//     additionally carries the RESOURCE REGION that the 409 body does not have
//     at all.
//
// So the two readings are not redundant: the 409 is authoritative about WHICH
// allocations block, and the loaded page is richer about WHERE they are.
const blockersFrom = (err, allocations) => {
  // `err.blockers` is kept as a second reading only because it costs one `??`
  // — the real carrier is `err.body`.
  const structured = err?.body?.blockers ?? err?.blockers;
  if (Array.isArray(structured) && structured.length > 0) {
    return structured.map((b) => ({
      allocationId: b.allocationId,
      cidr: b.cidr,
      resourceId: b.resourceId,
      resourceType: b.resourceType,
      accountId: b.accountId,
      resourceRegion: b.resourceRegion || '',
    }));
  }
  return (allocations || []).filter(isResourceBacked).map((a) => ({
    allocationId: a.allocationId,
    cidr: a.cidr,
    resourceId: a.allocatedResourceId,
    resourceType: a.resourceType,
    accountId: a.resourceOwnerId || a.ownerAccountId,
    resourceRegion: a.resourceRegion || a.region || '',
  }));
};

export default function IpamPoolDetail() {
  const { poolId } = useParams();
  const navigate = useNavigate();

  const [pool, setPool] = useState(null);
  const [cidrs, setCidrs] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [shares, setShares] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [historyCidr, setHistoryCidr] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [allocateOpen, setAllocateOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBlockers, setDeleteBlockers] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState(null);

  // THE IPAM'S OWN STATUS, READ HERE FOR THE FIRST TIME. Three values, and the
  // third is the reason this is not a boolean: 'live' | 'not_live' | 'unread'.
  // Only 'not_live' may switch a control off — a config read that did not land is
  // Prism not knowing, and a greyed control asserting "your IPAM is gone" out of
  // an unread field states something Prism never established. The service is the
  // authority either way and answers with a real sentence.
  const [ipamLiveness, setIpamLiveness] = useState('unread');

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const poolRes = await getIpamPool(poolId);
      setPool(poolRes?.data ?? null);
      // The three sub-lists hang off the {id} lookup, so a missing pool is a real
      // 404 on all four and there is no empty-page contract to honour here.
      const [cidrRes, allocRes, shareRes, scopeRes] = await Promise.all([
        listIpamPoolCidrs(poolId),
        listIpamPoolAllocations(poolId),
        listIpamPoolShares(poolId),
        // Only for the history drawer's caption. It answers an empty page rather
        // than an error for an unconfigured tenant, so it cannot fail this page.
        listIpamScopes(),
      ]);
      setCidrs(rowsOf(cidrRes));
      setAllocations(rowsOf(allocRes));
      setShares(rowsOf(shareRes));
      setScopes(rowsOf(scopeRes));
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [poolId]);

  useEffect(() => { load(); }, [load]);

  // Its own read, deliberately NOT folded into `load`: the four tables must not
  // fail into the red card because the config route hiccupped, and an unread
  // config is a real third answer rather than an error on this page.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getIpamConfig();
        if (cancelled) return;
        const status = res?.data?.status;
        if (!status) {
          // No config row on a successful read is the route's documented answer
          // for a tenant that never configured IPAM (200 + data:null) — but a row
          // with no status at all is unread, and neither is worth switching Edit
          // off over when the service will say so itself.
          setIpamLiveness(res?.data ? 'unread' : 'not_live');
          return;
        }
        setIpamLiveness(
          status === 'enabled' || status === 'partial_failure' ? 'live' : 'not_live'
        );
      } catch (_) {
        if (!cancelled) setIpamLiveness('unread');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const scopeLabel = useMemo(
    () => scopes.find((s) => s.scopeId === pool?.scopeId)?.description || '',
    [scopes, pool]
  );

  const isolated = ipamStateIsIsolated(pool?.state);
  const readOnly = Boolean(pool?.awsCreated || pool?.adopted || isolated);

  // ── EDIT AND DELETE ARE NOT THE SAME QUESTION ──────────────────────────────
  //
  // They were gated together on `readOnly`, which left Edit LIVE against a dead
  // IPAM: ModifyIpamPool is in ipamOpsNeedingALiveIpam, so the operator filled
  // the whole dialog and got a 400 out of it. DELETE IS DELIBERATELY LEFT ALONE
  // — the backend's own comment names DeleteIpamPool as part of the CLEANUP
  // surface that must never be gated, because refusing it "would leave a customer
  // account holding resources with no in-product way to remove them". A dead IPAM
  // must not be GROWN, and must always be cleanable.
  const editBlockedReason = (() => {
    if (readOnly && !isolated) return IPAM_POOL_EDIT_READ_ONLY;
    if (isolated) return IPAM_ISOLATED_SENTENCE;
    if (ipamLiveness === 'not_live') return IPAM_POOL_EDIT_NEEDS_LIVE_IPAM;
    return '';
  })();
  const editDisabled = !pool || !!editBlockedReason;

  // The freshness line every table on this page needed and only the Pool card
  // had. It hardens when AWS has stopped returning the pool — and it hardens a
  // second, different way when Prism holds NO liveness fact at all.
  //
  // THE CASE THIS SECOND ARM EXISTS FOR. `observed_missing` is written only by a
  // refresh that COMPLETED, so an IPAM deleted outside Prism — where the refresh
  // cannot complete — leaves the flag off and the ledger rows intact. If the
  // config read has not landed either, the page has nothing that can say the
  // pool is anything other than current, and it used to say exactly that. The
  // tables still render: a table Prism hides is indistinguishable from address
  // space the customer does not have. They are just no longer vouched for.
  const ledgerCaveat = ipamPoolLedgerCaveat(!!pool?.observedMissing, ipamLiveness);

  const liveCidrs = useMemo(() => cidrs.filter((c) => cidrIsLive(c.state)), [cidrs]);
  const blockers = useMemo(() => blockersFrom(null, allocations), [allocations]);
  const failedCidrs = useMemo(
    () => cidrs.filter((c) => String(c.state || '').toLowerCase().startsWith('failed-')),
    [cidrs]
  );

  // Every mutation funnels through here so a refusal is surfaced the same way
  // whichever button caused it, and so the four tables are always re-read
  // together — a CIDR deprovision changes the allocation list too.
  const runAction = useCallback(async (fn, successNote) => {
    setActionError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successNote);
      await load();
      return true;
    } catch (e) {
      setActionError(e.message);
      return false;
    }
  }, [load]);

  const header = (
    <PortalPageHeader
      icon={<LanIcon sx={{ fontSize: 24 }} />}
      title={pool?.description || 'IPAM pool'}
      color={IPAM_COLOR}
      subtitle={(
        <Box component="span" sx={{ fontFamily: FONT_MONO }}>{poolId}</Box>
      )}
      action={(
        <Box sx={actionRowSx}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/securityhub/ipam/pools')}
            sx={{ fontFamily: FONT_BODY, textTransform: 'none', borderColor: CK.border, color: CK.navy }}
          >
            All pools
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={refreshing}
            sx={{ fontFamily: FONT_BODY, textTransform: 'none', borderColor: CK.border, color: CK.navy }}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
          {/* A DISABLED CONTROL THAT DOES NOT SAY WHY IS THE DEFECT. The span is
              not decoration: a disabled MUI button sets pointer-events:none on
              itself, so the Tooltip has to hang off a wrapper or the reason never
              reaches the screen at all. */}
          <Tooltip title={editBlockedReason || ''}>
            <span>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditOpen(true)}
                disabled={editDisabled}
                sx={{ fontFamily: FONT_BODY, textTransform: 'none', borderColor: CK.border, color: CK.navy }}
              >
                Edit
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => { setDeleteBlockers(null); setDeleteMessage(null); setDeleteOpen(true); }}
            disabled={!pool || readOnly}
            sx={{ fontFamily: FONT_BODY, textTransform: 'none', borderColor: `${CK.red}55`, color: CK.red }}
          >
            Delete pool
          </Button>
        </Box>
      )}
    />
  );

  if (error) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={load} />
        </motion.div>
      </motion.div>
    );
  }

  if (loading || !pool) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading pool…" color={IPAM_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{header}</motion.div>

      {isolated && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mb: 3, borderRadius: '8px', fontFamily: FONT_BODY }}>
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              The AWS account that owns this pool has left the organization
            </AlertTitle>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>
              Prism cannot reach an account outside the organization: there is no grant to attach and
              no session to open, so every action on this page would fail however many times it is
              retried. The pool may still exist and still bill inside that account. Bringing the
              account back into the organization is the only path.
            </Typography>
          </Alert>
        </motion.div>
      )}

      {pool.observedMissing && (
        <motion.div variants={sectionVariants}>
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3, borderRadius: '8px', fontFamily: FONT_BODY }}>
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              AWS stopped returning this pool
            </AlertTitle>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>
              Prism has not deleted it. Either it is already gone — harmless — or it is an orphan
              that still exists in AWS and still occupies quota. The tables below are Prism’s last
              record of it, not a live read.
            </Typography>
          </Alert>
        </motion.div>
      )}

      {actionError && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" onClose={() => setActionError(null)} sx={{ mb: 3, borderRadius: '8px', fontFamily: FONT_BODY }}>
            {actionError}
          </Alert>
        </motion.div>
      )}
      {notice && (
        <motion.div variants={sectionVariants}>
          <Alert severity="success" onClose={() => setNotice(null)} sx={{ mb: 3, borderRadius: '8px', fontFamily: FONT_BODY }}>
            {notice}
          </Alert>
        </motion.div>
      )}

      {/* ── Identity ────────────────────────────────────────────────────── */}
      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={sectionCardSx}>
          <PortalSectionHeader
            icon={<LanIcon />}
            title="Pool"
            color={IPAM_COLOR}
            subtitle="What AWS reports about this pool, as of the last refresh Prism ran — not a live poll."
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Field label="State"><IpamStateChip state={pool.state} /></Field>
            <Field label="Scope"><Mono>{dash(pool.scopeId)}</Mono></Field>
            <Field label="Scope type">{dash(pool.scopeType)}</Field>
            <Field label="Address family"><Mono>{dash(pool.addressFamily)}</Mono></Field>
            <Field label="Locale">
              {pool.locale
                ? <Mono>{pool.locale}</Mono>
                : <Muted title="This pool has no locale, so AWS will not allocate its CIDRs to a VPC in any region. A locale cannot be added after the pool is created.">None</Muted>}
            </Field>
            <Field label="Region"><Mono>{dash(pool.region)}</Mono></Field>
            <Field label="Owner account"><Mono>{dash(pool.ownerAccountId)}</Mono></Field>
            <Field label="Depth">{pool.poolDepth || 1}</Field>
            <Field label="Carved out of">
              {pool.sourceIpamPoolId ? (
                <Button
                  size="small"
                  onClick={() => navigate(`/securityhub/ipam/pools/${encodeURIComponent(pool.sourceIpamPoolId)}`)}
                  sx={{ fontFamily: FONT_MONO, textTransform: 'none', p: 0, minWidth: 0, color: IPAM_COLOR }}
                >
                  {pool.sourceIpamPoolId}
                </Button>
              ) : <Muted title="This is a top-level pool: it sits directly under its scope.">Top level</Muted>}
            </Field>
            <Field label="Netmask (min / default / max)">
              <Mono>
                {`/${dash(pool.allocationMinNetmaskLength)} · /${dash(pool.allocationDefaultNetmaskLength)} · /${dash(pool.allocationMaxNetmaskLength)}`}
              </Mono>
            </Field>
            <Field label="Auto-import">
              <Tooltip title="Prism never sets auto-import on a pool it creates: it imports CIDRs that break the pool's own rules, keeps only the largest of any that overlap, and breaks ties at random. It is shown here read-only because a pool that already has it behaves that way whether or not Prism approves.">
                <Chip
                  size="small"
                  label={pool.autoImport ? 'On (read-only)' : 'Off'}
                  sx={statusChipSx(pool.autoImport ? 'warning' : 'neutral')}
                />
              </Tooltip>
            </Field>
          </Box>

          {pool.stateMessage && (
            <Box sx={{ ...insetPanelSx, mt: 2.5 }}>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8125rem', color: CK.body }}>
                <b>AWS state message:</b> {pool.stateMessage}
              </Typography>
            </Box>
          )}

          {readOnly && !isolated && (
            <Box sx={{ ...insetPanelSx, mt: 2.5 }}>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8125rem', color: CK.body }}>
                {pool.awsCreated
                  ? 'AWS created this pool inside the IPAM. It cannot be edited or deleted on its own.'
                  : 'The customer created this pool and Prism adopted it. Prism will never delete it — doing so would destroy allocation history that is the customer’s, not Prism’s.'}
              </Typography>
            </Box>
          )}
        </Paper>
      </motion.div>

      {/* ── 1. Provisioned CIDRs ────────────────────────────────────────── */}
      <motion.div variants={sectionVariants} style={{ marginTop: 24 }}>
        <Paper elevation={0} sx={sectionCardSx}>
          <PortalSectionHeader
            icon={<StorageOutlinedIcon />}
            title="Provisioned CIDRs"
            color={IPAM_COLOR}
            subtitle={`${liveCidrs.length} ${liveCidrs.length === 1 ? 'CIDR is' : 'CIDRs are'} in this pool. A CIDR still counts while it is deprovisioning or after a deprovision failed — only "Deprovisioned" means it has left. ${ledgerCaveat}`}
            action={(
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setProvisionOpen(true)}
                disabled={readOnly}
                sx={{ fontFamily: FONT_BODY, textTransform: 'none', borderColor: CK.border, color: CK.navy }}
              >
                Provision CIDR
              </Button>
            )}
          />

          {failedCidrs.length > 0 && (
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2, borderRadius: '8px', fontFamily: FONT_BODY }}>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>
                {failedCidrs.length} {failedCidrs.length === 1 ? 'CIDR' : 'CIDRs'} finished in a
                failed state. A failed <b>deprovision</b> leaves the CIDR in the pool, still counting
                against its quota, and is retryable — usually it failed because the pool still holds
                allocations. A failed <b>provision</b> added nothing. The reason is on the row.
              </Typography>
            </Alert>
          )}

          <DataTable
            columns={['CIDR', 'State', 'Netmask', 'Region', 'Owner', '']}
            minWidth={880}
            empty="No CIDRs have been provisioned into this pool yet, so nothing can be allocated from it."
            rows={cidrs}
            renderRow={(c, i) => (
              <TableRow key={c.cidr || c.resourceId || i} hover sx={rowSx(i)}>
                <TableCell sx={cidrCellSx}>{c.cidr}</TableCell>
                <TableCell sx={bodyCellSx}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                    <IpamStateChip state={c.state} />
                    {/* THE FAILURE REASON IS ON THE ROW, NOT BEHIND A HOVER. For a
                        failed CIDR it is the entire diagnosis, and a row rendered
                        as "failed" with no cause is a support ticket Prism could
                        have answered itself. */}
                    {(c.failureCode || c.failureMessage) && (
                      <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.red }}>
                        {[c.failureCode, c.failureMessage].filter(Boolean).join(': ')}
                      </Typography>
                    )}
                    {c.observedMissing && (
                      <Tooltip title="AWS stopped returning this CIDR and Prism has not deprovisioned it.">
                        <Chip size="small" label="Not seen in AWS" sx={statusChipSx('warning')} />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={bodyCellSx}>{c.netmaskLength ? <Mono>{`/${c.netmaskLength}`}</Mono> : <Muted />}</TableCell>
                <TableCell sx={bodyCellSx}><Mono>{dash(c.region)}</Mono></TableCell>
                <TableCell sx={bodyCellSx}><Mono>{dash(c.ownerAccountId)}</Mono></TableCell>
                <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }} align="right">
                  <HistoryButton
                    cidr={c.cidr}
                    scopeId={pool.scopeId}
                    onOpen={setHistoryCidr}
                  />
                  <Button
                    size="small"
                    disabled={readOnly || !cidrIsLive(c.state) || c.adopted}
                    onClick={() => setConfirm({
                      title: 'Deprovision CIDR',
                      body: c.adopted
                        ? null
                        : `${c.cidr} leaves this pool. Nothing in your accounts changes — no VPC loses an address and no resource is touched. AWS answers "pending-deprovision" and can still settle to "failed-deprovision" minutes later, most often because the pool still holds allocations.`,
                      confirmLabel: 'Deprovision',
                      danger: true,
                      run: () => runAction(
                        () => deprovisionIpamPoolCidr(poolId, c.cidr),
                        `Deprovision of ${c.cidr} started. AWS reports the result within a minute or two.`
                      ),
                    })}
                    sx={{ fontFamily: FONT_BODY, textTransform: 'none', color: CK.red }}
                  >
                    Deprovision
                  </Button>
                </TableCell>
              </TableRow>
            )}
          />
        </Paper>
      </motion.div>

      {/* ── 2. Allocations ──────────────────────────────────────────────── */}
      <motion.div variants={sectionVariants} style={{ marginTop: 24 }}>
        <Paper elevation={0} sx={sectionCardSx}>
          <PortalSectionHeader
            icon={<StorageOutlinedIcon />}
            title="Allocations"
            color={IPAM_COLOR}
            subtitle={`${IPAM_POOL_ALLOCATIONS_SUBTITLE} ${ledgerCaveat}`}
            action={(
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAllocateOpen(true)}
                disabled={readOnly}
                sx={{ fontFamily: FONT_BODY, textTransform: 'none', borderColor: CK.border, color: CK.navy }}
              >
                Allocate CIDR
              </Button>
            )}
          />
          <DataTable
            columns={['CIDR', 'Source', 'Resource', 'Account', 'Region', 'State', '']}
            minWidth={1040}
            empty={IPAM_POOL_ALLOCATIONS_EMPTY}
            rows={allocations}
            renderRow={(a, i) => {
              const backed = isResourceBacked(a);
              return (
                <TableRow key={a.allocationId || i} hover sx={rowSx(i)}>
                  <TableCell sx={cidrCellSx}>{a.cidr}</TableCell>
                  <TableCell sx={bodyCellSx}>
                    <Tooltip title={backed
                      ? 'AWS created this allocation because a real resource took the space. It disappears on its own when the resource is deleted, and AWS refuses every attempt to release it directly.'
                      : 'Someone reserved this space by hand. It is not backed by a resource, so it can be released.'}
                    >
                      <Chip
                        size="small"
                        label={backed ? 'Resource-backed' : 'Manual'}
                        sx={statusChipSx(backed ? 'warning' : 'info')}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {a.allocatedResourceId ? (
                      <Box>
                        <Mono>{a.allocatedResourceId}</Mono>
                        <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted }}>
                          {dash(a.resourceType)}
                        </Typography>
                      </Box>
                    ) : <Muted title="No resource is attached to this allocation.">{dash(a.description)}</Muted>}
                  </TableCell>
                  <TableCell sx={bodyCellSx}><Mono>{dash(a.resourceOwnerId || a.ownerAccountId)}</Mono></TableCell>
                  <TableCell sx={bodyCellSx}><Mono>{dash(a.resourceRegion || a.region)}</Mono></TableCell>
                  <TableCell sx={bodyCellSx}>
                    {ipamStateMeta(a.state) ? <IpamStateChip state={a.state} /> : <Muted title="AWS does not report a lifecycle state for an allocation." />}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }} align="right">
                    <HistoryButton
                      cidr={a.cidr}
                      scopeId={pool.scopeId}
                      onOpen={setHistoryCidr}
                    />
                    {/* A DISABLED BUTTON WITH NO EXPLANATION READS AS A PERMISSION
                        BUG. The tooltip is not decoration: it is the difference
                        between an operator filing a ticket and an operator
                        deleting the VPC. */}
                    <Tooltip title={backed
                      ? `AWS refuses to release an allocation that a live resource is using, and there is no force option — its own instruction is to delete the resource. ${NEVER_DELETE_SENTENCE} Delete ${a.allocatedResourceId || 'the resource'} in account ${a.resourceOwnerId || a.ownerAccountId || 'that owns it'} and IPAM releases this allocation on its own, a few minutes later.`
                      : 'Releases this reservation. Nothing in your accounts changes.'}
                    >
                      <span>
                        <Button
                          size="small"
                          disabled={readOnly || backed}
                          onClick={() => setConfirm({
                            title: 'Release allocation',
                            body: `${a.cidr} goes back to the pool as free space. Nothing in your accounts changes — no resource is touched.`,
                            confirmLabel: 'Release',
                            danger: true,
                            run: () => runAction(
                              () => releaseIpamPoolAllocation(poolId, a.allocationId),
                              `Release of ${a.cidr} started.`
                            ),
                          })}
                          sx={{ fontFamily: FONT_BODY, textTransform: 'none', color: CK.red }}
                        >
                          Release
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            }}
          />
        </Paper>
      </motion.div>

      {/* ── 3. RAM shares ───────────────────────────────────────────────── */}
      <motion.div variants={sectionVariants} style={{ marginTop: 24 }}>
        <Paper elevation={0} sx={sectionCardSx}>
          <PortalSectionHeader
            icon={<ShareOutlinedIcon />}
            title="Shared with"
            color={IPAM_COLOR}
            subtitle={'Resource Access Manager shares that let other accounts allocate out of this pool. A share to an '
              + `account outside the organization lands as an invitation and does nothing until that account accepts it. ${ledgerCaveat}`}
            action={(
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setShareOpen(true)}
                disabled={readOnly}
                sx={{ fontFamily: FONT_BODY, textTransform: 'none', borderColor: CK.border, color: CK.navy }}
              >
                Share pool
              </Button>
            )}
          />
          <DataTable
            columns={['Name', 'Status', 'Shared with', 'Region', '']}
            minWidth={880}
            empty="This pool is not shared. Only its owning account can allocate out of it."
            rows={shares}
            renderRow={(s, i) => (
              <TableRow key={s.shareArn || i} hover sx={rowSx(i)}>
                <TableCell sx={bodyCellSx}>
                  <Box>
                    <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.navy }}>
                      {dash(s.name)}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.7rem', color: CK.muted, wordBreak: 'break-all' }}>
                      {s.shareArn}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                    <IpamStateChip state={s.status} />
                    {/* An EMPTY resourceId means RAM has this share and Prism has
                        no row for it — the orphan the tag sweep recovers, not a
                        cosmetic gap. */}
                    {s.recorded === false && (
                      <Tooltip title="This share exists in AWS Resource Access Manager but Prism has no record of it, so teardown would not remove it. It was most likely created outside Prism.">
                        <Chip size="small" label="Not recorded by Prism" sx={statusChipSx('warning')} />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  {(s.sharedWithAccountIds || []).length > 0
                    ? (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {s.sharedWithAccountIds.map((id) => (
                          <Chip key={id} size="small" label={id} sx={{ ...statusChipSx('neutral'), fontFamily: FONT_MONO }} />
                        ))}
                      </Box>
                    )
                    : <Muted title="AWS did not report any principals on this share." />}
                </TableCell>
                <TableCell sx={bodyCellSx}><Mono>{dash(s.region)}</Mono></TableCell>
                <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }} align="right">
                  <Button
                    size="small"
                    disabled={readOnly}
                    onClick={() => setConfirm({
                      title: 'Stop sharing this pool',
                      body: 'The accounts on this share lose the ability to allocate new space out of the pool. Allocations they have already made are NOT released and the resources behind them are not touched.',
                      confirmLabel: 'Stop sharing',
                      danger: true,
                      run: () => runAction(
                        () => deleteIpamPoolShare(poolId, s.shareArn),
                        'The share is being deleted.'
                      ),
                    })}
                    sx={{ fontFamily: FONT_BODY, textTransform: 'none', color: CK.red }}
                  >
                    Stop sharing
                  </Button>
                </TableCell>
              </TableRow>
            )}
          />
        </Paper>
      </motion.div>

      {/* ── 4. Blockers ─────────────────────────────────────────────────── */}
      <motion.div variants={sectionVariants} style={{ marginTop: 24 }}>
        <Paper elevation={0} sx={sectionCardSx}>
          <PortalSectionHeader
            icon={<BlockIcon />}
            title="What stands in the way of deleting this pool"
            color={blockers.length > 0 ? CK.amber : IPAM_COLOR}
            subtitle={blockers.length > 0
              ? 'These resources are using space from this pool. AWS provides no way to force-release them, '
                + `so the pool cannot be deleted until they are gone. ${ledgerCaveat}`
              // THE LIST BEHIND THIS VERDICT STRUCTURALLY CANNOT HOLD EVERY
              // BLOCKER. `blockers` is derived from listIpamPoolAllocations, whose
              // service method is LEDGER ONLY — "the customer's own allocations
              // live in the IpamPoolAllocation cache the refresh owns". The DELETE
              // probes AWS live for EVERY allocation and refuses on any whose
              // source is `resource`, so a pool blocked by a member's own
              // resource-backed allocation rendered green here and 409'd a moment
              // later. The sentence is now scoped to what the list covers.
              : `${IPAM_POOL_NO_PRISM_BLOCKERS} ${ledgerCaveat}`}
          />
          {blockers.length > 0 ? (
            <>
              <Box sx={{ ...insetPanelSx, mb: 2 }}>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body }}>
                  <b>{NEVER_DELETE_SENTENCE}</b> AWS’s own answer to a resource-backed allocation is
                  “delete the resource”, and that is a line Prism does not cross — the resources
                  below are yours and may not even be in this account. Delete each one where it
                  lives and IPAM releases its allocation automatically. Expect a wait: after a VPC is
                  deleted the allocation lingers as a ghost for <b>several minutes</b>, and a release
                  attempted during that window is still refused.
                </Typography>
              </Box>
              <DataTable
                columns={['Resource', 'Type', 'CIDR', 'Account', 'Region']}
                minWidth={880}
                rows={blockers}
                renderRow={(b, i) => (
                  <TableRow key={b.allocationId || `${b.resourceId}:${i}`} hover sx={rowSx(i)}>
                    <TableCell sx={cidrCellSx}>{dash(b.resourceId)}</TableCell>
                    <TableCell sx={bodyCellSx}>{dash(b.resourceType)}</TableCell>
                    <TableCell sx={cidrCellSx}>{dash(b.cidr)}</TableCell>
                    <TableCell sx={cidrCellSx}>{dash(b.accountId)}</TableCell>
                    <TableCell sx={bodyCellSx}><Mono>{dash(b.resourceRegion)}</Mono></TableCell>
                  </TableRow>
                )}
              />
            </>
          ) : (
            <Typography sx={{ ...emptyTextSx, fontSize: '0.875rem' }}>
              {IPAM_POOL_NO_PRISM_BLOCKERS}
            </Typography>
          )}
        </Paper>
      </motion.div>

      {/* ── Dialogs ─────────────────────────────────────────────────────── */}

      <ConfirmDialog
        confirm={confirm}
        onClose={() => setConfirm(null)}
      />

      <EditPoolDialog
        open={editOpen}
        pool={pool}
        onClose={() => setEditOpen(false)}
        onSaved={async (body) => {
          const ok = await runAction(() => updateIpamPool(poolId, body), 'Pool updated.');
          if (ok) setEditOpen(false);
          return ok;
        }}
      />

      <CidrDialog
        open={provisionOpen}
        title="Provision a CIDR into this pool"
        confirmLabel="Provision"
        blurb="AWS answers straight away with “pending-provision” and settles in about twenty seconds. Prism does not wait — the row appears immediately at whatever state AWS returned and updates on the next refresh."
        withDescription={false}
        onClose={() => setProvisionOpen(false)}
        onSubmit={async ({ cidr, netmaskLength }) => {
          const ok = await runAction(
            () => provisionIpamPoolCidr(poolId, { cidr: cidr || undefined, netmaskLength }),
            'Provisioning started.'
          );
          if (ok) setProvisionOpen(false);
          return ok;
        }}
      />

      <CidrDialog
        open={allocateOpen}
        title="Allocate space out of this pool"
        confirmLabel="Allocate"
        blurb="A manual allocation reserves space so nothing else can take it. It creates no VPC and no subnet — it only marks the range as spoken for, and it can be released again."
        withDescription
        onClose={() => setAllocateOpen(false)}
        onSubmit={async ({ cidr, netmaskLength, description }) => {
          const ok = await runAction(
            () => allocateIpamPoolCidr(poolId, { cidr: cidr || undefined, netmaskLength, description }),
            'Allocation created.'
          );
          if (ok) setAllocateOpen(false);
          return ok;
        }}
      />

      <ShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        onSubmit={async (principals) => {
          const ok = await runAction(
            () => createIpamPoolShare(poolId, principals),
            'Share created. An account outside the organization has to accept the invitation before it can use the pool.'
          );
          if (ok) setShareOpen(false);
          return ok;
        }}
      />

      <DeletePoolDialog
        open={deleteOpen}
        pool={pool}
        knownBlockers={blockers}
        refusalBlockers={deleteBlockers}
        refusalMessage={deleteMessage}
        onClose={() => setDeleteOpen(false)}
        onDelete={async () => {
          setActionError(null);
          try {
            await deleteIpamPool(poolId);
            setDeleteOpen(false);
            navigate('/securityhub/ipam/pools');
          } catch (e) {
            // THE REFUSAL STAYS IN THE DIALOG, NAMED. Closing the dialog and
            // dropping a red banner on the page would lose the one thing that
            // makes this refusal actionable: which resource, in which account.
            setDeleteMessage(e.message);
            setDeleteBlockers(blockersFrom(e, allocations));
          }
        }}
      />

      {/* `open` is gated on BOTH values the drawer needs, not just the CIDR: an
          unscoped pool has no scopeId, and opening the drawer on one would show
          a panel that can never look anything up. The History buttons above are
          disabled for the same reason, so the state is unreachable rather than
          merely handled. */}
      <IpamHistoryDrawer
        open={Boolean(historyCidr && pool.scopeId)}
        onClose={() => setHistoryCidr(null)}
        cidr={historyCidr}
        scopeId={pool.scopeId}
        scopeLabel={scopeLabel}
      />
    </motion.div>
  );
}

// ── Small presentational helpers ────────────────────────────────────────────

function Field({ label, children }) {
  return (
    <Box sx={{ minWidth: 160 }}>
      <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted, mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.navy }}>{children}</Box>
    </Box>
  );
}

// The history affordance, in one place because both the CIDR table and the
// allocation table offer it and both have the same precondition.
//
// AWS keys address history by CIDR *and* SCOPE, so a pool with no resolved scope
// has nothing to look the CIDR up in. The drawer already refuses that case by
// rendering "nothing to look up" and making no request; disabling the button
// with the reason is better than letting an operator open a panel that can only
// tell them it cannot help.
function HistoryButton({ cidr, scopeId, onOpen }) {
  const usable = Boolean(cidr && scopeId);
  return (
    <Tooltip title={usable
      ? 'Every VPC, subnet and instance that has held this address range, as far back as IPAM recorded it.'
      : 'Address history is looked up by CIDR within a scope, and Prism could not resolve a scope for this pool — so there is nothing to look this range up in.'}
    >
      <span>
        <Button
          size="small"
          disabled={!usable}
          startIcon={<HistoryOutlinedIcon />}
          onClick={() => onOpen(cidr)}
          sx={{ fontFamily: FONT_BODY, textTransform: 'none', color: IPAM_COLOR }}
        >
          History
        </Button>
      </span>
    </Tooltip>
  );
}

// One table shell for all four tables, so a column cannot pick up a different
// border or a different empty sentence depending on which section it is in.
function DataTable({ columns, rows, renderRow, empty, minWidth = 880 }) {
  if (!rows || rows.length === 0) {
    return (
      <Typography sx={{ ...emptyTextSx, fontSize: '0.875rem' }}>{empty}</Typography>
    );
  }
  return (
    <TableContainer component={Paper} elevation={0} sx={{ ...listCardSx, overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth }}>
        <TableHead>
          <TableRow sx={tableHeadRowSx}>
            {columns.map((c, i) => (
              <TableCell key={c || i} sx={headCellSx} align={i === columns.length - 1 && c === '' ? 'right' : 'left'}>
                {c}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{rows.map(renderRow)}</TableBody>
      </Table>
    </TableContainer>
  );
}

function ConfirmDialog({ confirm, onClose }) {
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (confirm) setBusy(false); }, [confirm]);
  if (!confirm) return null;
  return (
    <Dialog open onClose={busy ? undefined : onClose} {...dialogProps}>
      <DialogTitle sx={dialogTitleSx}>{confirm.title}</DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body }}>
          {confirm.body}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ fontFamily: FONT_BODY, textTransform: 'none', color: CK.body }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            await confirm.run();
            onClose();
          }}
          sx={{
            fontFamily: FONT_BODY,
            textTransform: 'none',
            backgroundColor: confirm.danger ? CK.red : IPAM_COLOR,
            '&:hover': { backgroundColor: confirm.danger ? CK.redDark : IPAM_COLOR_DARK },
          }}
        >
          {busy ? 'Working…' : confirm.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Provision / allocate ────────────────────────────────────────────────────
//
// One dialog for both, because the two requests are the same shape and a
// second near-identical form is a second place for the netmask rule to be
// spelled differently.
//
// NEITHER CARRIES A REGION, AND THAT ABSENCE IS STRUCTURAL. The allocation plane
// is locale-bound — a call sent to the IPAM's home Region answers
// "InvalidParameterValue: the pool is not in the current Region" — so the Region
// is a property of the pool row that the server derives. A field here could only
// ever produce the same answer or a wrong one, and the wrong one would fail deep
// inside an AWS call instead of at the edge.
function CidrDialog({ open, title, blurb, confirmLabel, withDescription, onClose, onSubmit }) {
  const [cidr, setCidr] = useState('');
  const [netmask, setNetmask] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCidr('');
    setNetmask('');
    setDescription('');
    setBusy(false);
  }, [open]);

  const netmaskLength = useMemo(() => {
    const trimmed = netmask.trim();
    if (trimmed === '') return undefined;
    const n = Number(trimmed);
    return Number.isFinite(n) ? Math.trunc(n) : undefined;
  }, [netmask]);

  const canSubmit = (cidr.trim() !== '' || netmaskLength !== undefined) && !busy;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} {...dialogProps}>
      <DialogTitle sx={dialogTitleSx}>{title}</DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: 2 }}>
          {blurb}
        </Typography>
        <TextField
          fullWidth
          size="small"
          label="CIDR"
          placeholder="10.100.0.0/16"
          value={cidr}
          onChange={(e) => setCidr(e.target.value)}
          sx={{ mb: 2, '& .MuiInputBase-input': { fontFamily: FONT_MONO } }}
          InputLabelProps={{ sx: { fontFamily: FONT_BODY } }}
        />
        <TextField
          fullWidth
          size="small"
          type="number"
          label="…or a netmask length, and let IPAM pick the range"
          value={netmask}
          onChange={(e) => setNetmask(e.target.value)}
          sx={{ mb: 2, '& .MuiInputBase-input': { fontFamily: FONT_MONO } }}
          InputLabelProps={{ sx: { fontFamily: FONT_BODY } }}
        />
        {withDescription && (
          <TextField
            fullWidth
            size="small"
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ mb: 2, '& .MuiInputBase-input': { fontFamily: FONT_BODY } }}
            InputLabelProps={{ sx: { fontFamily: FONT_BODY } }}
          />
        )}
        <Box sx={insetPanelSx}>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8125rem', color: CK.body }}>
            AWS validates the range itself and Prism passes its refusal through word for word. IPAM
            rejects some syntactically valid IPv6 ULA ranges that no client-side check would catch,
            so a rejection here is AWS’s, stated in AWS’s own words.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ fontFamily: FONT_BODY, textTransform: 'none', color: CK.body }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={async () => {
            setBusy(true);
            const ok = await onSubmit({ cidr: cidr.trim(), netmaskLength, description: description.trim() });
            if (!ok) setBusy(false);
          }}
          sx={{
            fontFamily: FONT_BODY,
            textTransform: 'none',
            backgroundColor: IPAM_COLOR,
            '&:hover': { backgroundColor: IPAM_COLOR_DARK },
          }}
        >
          {busy ? 'Working…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Share ───────────────────────────────────────────────────────────────────
//
// PRINCIPALS ARE ACCOUNT IDS. Not OU ids and not organization ARNs: an OU
// principal would share the pool with every account that OU ever gains, silently
// and forever, and Prism deliberately stays out of AWS Organizations so it could
// not enumerate them to tell anyone what it had just done.
function ShareDialog({ open, onClose, onSubmit }) {
  const [raw, setRaw] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setRaw(''); setBusy(false); } }, [open]);

  const principals = useMemo(
    () => raw.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean),
    [raw]
  );
  const malformed = principals.filter((p) => !/^\d{12}$/.test(p));

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} {...dialogProps}>
      <DialogTitle sx={dialogTitleSx}>Share this pool</DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: 2 }}>
          The accounts you name can allocate space out of this pool. Sharing grants nothing else —
          it does not let them change the pool, and it creates nothing in their accounts.
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          label="Account IDs"
          placeholder="123456789012, 210987654321"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          sx={{ mb: 2, '& .MuiInputBase-input': { fontFamily: FONT_MONO } }}
          InputLabelProps={{ sx: { fontFamily: FONT_BODY } }}
        />
        {malformed.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px', fontFamily: FONT_BODY }}>
            {malformed.join(', ')} {malformed.length === 1 ? 'is not' : 'are not'} a 12-digit account
            ID. Prism shares with accounts only — an organizational unit would silently share this
            pool with every account that OU ever gains.
          </Alert>
        )}
        <Box sx={insetPanelSx}>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8125rem', color: CK.body }}>
            An account outside this organization receives an <b>invitation</b> rather than a live
            share, and cannot use the pool until it accepts. That is normal, not a failure.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ fontFamily: FONT_BODY, textTransform: 'none', color: CK.body }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={busy || principals.length === 0 || malformed.length > 0}
          onClick={async () => {
            setBusy(true);
            const ok = await onSubmit(principals);
            if (!ok) setBusy(false);
          }}
          sx={{
            fontFamily: FONT_BODY,
            textTransform: 'none',
            backgroundColor: IPAM_COLOR,
            '&:hover': { backgroundColor: IPAM_COLOR_DARK },
          }}
        >
          {busy ? 'Sharing…' : 'Share'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Edit ────────────────────────────────────────────────────────────────────
//
// Every field on IpamPoolUpdate is a POINTER server-side, so an omitted key
// means "leave it alone" and an explicit value means "set it". Only what the
// operator actually changed is sent.
//
// Auto-import is on the request struct but is REFUSED rather than applied — it
// is there so the refusal can name it — so it is not offered here at all.
function EditPoolDialog({ open, pool, onClose, onSaved }) {
  const [description, setDescription] = useState('');
  const [minNetmask, setMinNetmask] = useState('');
  const [maxNetmask, setMaxNetmask] = useState('');
  const [defaultNetmask, setDefaultNetmask] = useState('');
  const [busy, setBusy] = useState(false);

  const asField = (n) => (n === null || n === undefined || n === 0 ? '' : String(n));

  // KEYED ON PRIMITIVES, NOT ON `pool`. The pool object gets a new identity on
  // every reload, and the reload that follows a FAILED save would otherwise wipe
  // the operator's edits out from under the error message they are still reading
  // — leaving them looking at a refusal with no way to see what they had typed.
  const seedDescription = pool?.description || '';
  const seedMin = asField(pool?.allocationMinNetmaskLength);
  const seedMax = asField(pool?.allocationMaxNetmaskLength);
  const seedDefault = asField(pool?.allocationDefaultNetmaskLength);

  useEffect(() => {
    if (!open) return;
    setDescription(seedDescription);
    setMinNetmask(seedMin);
    setMaxNetmask(seedMax);
    setDefaultNetmask(seedDefault);
    setBusy(false);
  }, [open, seedDescription, seedMin, seedMax, seedDefault]);

  const changedNumber = (raw, original) => {
    const trimmed = String(raw).trim();
    if (trimmed === asField(original)) return undefined;
    if (trimmed === '') return undefined;
    const n = Number(trimmed);
    return Number.isFinite(n) ? Math.trunc(n) : undefined;
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} {...dialogProps}>
      <DialogTitle sx={dialogTitleSx}>Edit pool</DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <TextField
          fullWidth
          size="small"
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2, mt: 1, '& .MuiInputBase-input': { fontFamily: FONT_BODY } }}
          InputLabelProps={{ sx: { fontFamily: FONT_BODY } }}
        />
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            size="small"
            type="number"
            label="Min netmask"
            value={minNetmask}
            onChange={(e) => setMinNetmask(e.target.value)}
            sx={{ flex: '1 1 140px', '& .MuiInputBase-input': { fontFamily: FONT_MONO } }}
            InputLabelProps={{ sx: { fontFamily: FONT_BODY } }}
          />
          <TextField
            size="small"
            type="number"
            label="Max netmask"
            value={maxNetmask}
            onChange={(e) => setMaxNetmask(e.target.value)}
            sx={{ flex: '1 1 140px', '& .MuiInputBase-input': { fontFamily: FONT_MONO } }}
            InputLabelProps={{ sx: { fontFamily: FONT_BODY } }}
          />
          <TextField
            size="small"
            type="number"
            label="Default netmask"
            value={defaultNetmask}
            onChange={(e) => setDefaultNetmask(e.target.value)}
            sx={{ flex: '1 1 140px', '& .MuiInputBase-input': { fontFamily: FONT_MONO } }}
            InputLabelProps={{ sx: { fontFamily: FONT_BODY } }}
          />
        </Box>
        <Box sx={insetPanelSx}>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8125rem', color: CK.body }}>
            The scope, address family and locale of a pool are fixed by AWS at create time and
            cannot be edited. Existing allocations are not re-checked against a new netmask rule —
            it applies to allocations made from now on.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ fontFamily: FONT_BODY, textTransform: 'none', color: CK.body }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            const ok = await onSaved({
              description: description === (pool?.description || '') ? undefined : description,
              allocationMinNetmaskLength: changedNumber(minNetmask, pool?.allocationMinNetmaskLength),
              allocationMaxNetmaskLength: changedNumber(maxNetmask, pool?.allocationMaxNetmaskLength),
              allocationDefaultNetmaskLength: changedNumber(defaultNetmask, pool?.allocationDefaultNetmaskLength),
            });
            if (!ok) setBusy(false);
          }}
          sx={{
            fontFamily: FONT_BODY,
            textTransform: 'none',
            backgroundColor: IPAM_COLOR,
            '&:hover': { backgroundColor: IPAM_COLOR_DARK },
          }}
        >
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Delete, and the refusal that has to name names ──────────────────────────
//
// A REFUSAL THAT NAMES NOTHING IS INDISTINGUISHABLE FROM A BUG, and this one is
// refusing for a good reason. The dialog therefore carries three things a bare
// "could not delete pool" would not:
//
//   * the resources standing in the way — id, type, CIDR, account, region —
//     because the operator's next action is "delete that VPC", possibly in an
//     account that is not even theirs;
//   * the promise that Prism will not do it for them, so the refusal reads as a
//     boundary rather than a failure;
//   * the several-minute ghost window, because "try again shortly" is literally
//     true here: after the VPC is deleted the allocation persists and release
//     keeps refusing until AWS catches up.
//
// The blockers are also shown BEFORE the button is pressed whenever this page
// already knows about them, so the refusal is rarely a surprise at all.
function DeletePoolDialog({ open, pool, knownBlockers, refusalBlockers, refusalMessage, onClose, onDelete }) {
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setBusy(false); }, [open]);

  const shown = refusalBlockers && refusalBlockers.length > 0 ? refusalBlockers : knownBlockers;
  const refused = Boolean(refusalMessage);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} {...dialogProps} maxWidth="md">
      <DialogTitle sx={dialogTitleSx}>
        {refused ? 'This pool cannot be deleted yet' : 'Delete pool'}
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {refused && (
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2, borderRadius: '8px', fontFamily: FONT_BODY }}>
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              AWS refused the delete
            </AlertTitle>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>
              {refusalMessage}
            </Typography>
          </Alert>
        )}

        <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: 2 }}>
          Deleting <Box component="span" sx={{ fontFamily: FONT_MONO }}>{pool?.poolId}</Box> removes
          the pool and the record of what was handed out of it. {/* Imported, not retyped: this
          sentence is quoted verbatim in the spec and grepped for by the verification step, and the
          reflowed copy that used to live here carried a typographic apostrophe, so the check missed
          it. */}
          <b>{IPAM_NO_VPC_DELETED}</b> This does not cascade — the pool’s CIDRs must already be
          deprovisioned and its allocations released.
        </Typography>

        {shown.length > 0 && (
          <>
            <Box sx={{ ...insetPanelSx, mb: 2 }}>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body }}>
                <b>{NEVER_DELETE_SENTENCE}</b> AWS offers no way to force-release an allocation a
                live resource is using — its own instruction is to delete the resource — so the only
                path is to delete each one below where it lives. IPAM then releases the allocation
                by itself. <b>Allow several minutes:</b> after a VPC is deleted its allocation
                lingers, and a release attempted inside that window is still refused. Coming back
                shortly and trying again is the correct thing to do, not a workaround.
              </Typography>
            </Box>
            <TableContainer component={Paper} elevation={0} sx={{ ...listCardSx, overflowX: 'auto', mb: 2 }}>
              <Table size="small" sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow sx={tableHeadRowSx}>
                    <TableCell sx={headCellSx}>Resource</TableCell>
                    <TableCell sx={headCellSx}>Type</TableCell>
                    <TableCell sx={headCellSx}>CIDR</TableCell>
                    <TableCell sx={headCellSx}>Account</TableCell>
                    <TableCell sx={headCellSx}>Region</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shown.map((b, i) => (
                    <TableRow key={b.allocationId || `${b.resourceId}:${i}`} sx={rowSx(i)}>
                      <TableCell sx={cidrCellSx}>{dash(b.resourceId)}</TableCell>
                      <TableCell sx={bodyCellSx}>{dash(b.resourceType)}</TableCell>
                      <TableCell sx={cidrCellSx}>{dash(b.cidr)}</TableCell>
                      <TableCell sx={cidrCellSx}>{dash(b.accountId)}</TableCell>
                      <TableCell sx={bodyCellSx}><Mono>{dash(b.resourceRegion)}</Mono></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ fontFamily: FONT_BODY, textTransform: 'none', color: CK.body }}>
          Close
        </Button>
        <Button
          variant="contained"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            await onDelete();
            setBusy(false);
          }}
          sx={{
            fontFamily: FONT_BODY,
            textTransform: 'none',
            backgroundColor: CK.red,
            '&:hover': { backgroundColor: CK.redDark },
          }}
        >
          {busy ? 'Deleting…' : refused ? 'Try again' : 'Delete pool'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
